import React, { FC } from "react";
import {
  VictoryChart,
  VictoryTheme,
  VictoryLabel,
  VictoryAxis,
  VictoryLine,
  VictoryGroup,
  VictoryLegend,
  VictoryArea,
} from "victory";
import { Price, PriceRange, isPriceRange } from "../../data-types";

type ChartDatum = {
  x: Date | string;
  y: number;
};

type EvaChartProps = {
  title: string;
  data: ChartDatum[];
  completionLogs: ChartDatum[];
  completionPercentage: number;
  plannedFee: Price | PriceRange;
  startDate?: Date;
  deadline?: Date;
  currencySymbol: string;
};

const formatDate = (date: Date) => {
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const formatCurrency = (amount: number, currencySymbol: string) => {
  return `${currencySymbol}${Math.round(amount).toLocaleString()}`;
};

const generateScurveData = (
  startDate: Date | null,
  endDate: Date | null,
  plannedFee: Price | PriceRange,
  numPoints: number = 30
) => {
  if (!startDate || !endDate) return [];

  // Add validation for dates
  if (endDate.getTime() <= startDate.getTime()) {
    console.warn("End date must be after start date");
    return [];
  }

  const totalDuration = endDate.getTime() - startDate.getTime();
  if (totalDuration < 1000 * 60 * 60 * 24) {
    console.warn("Duration must be at least 1 day");
    return [];
  }

  // Denormalize back to actual time
  const denormalizeTime = (t: number) => startDate.getTime() + ((t + 1) * totalDuration) / 2;

  const steepness = 5; // Adjust this value to change the curve shape
  const normalizedMidpoint = 0; // This is 0 in normalized space

  const maxValue = isPriceRange(plannedFee) ? plannedFee.upper ?? 0 : plannedFee.amount;
  const minValue = isPriceRange(plannedFee) ? plannedFee.lower ?? 0 : undefined;

  const step = 2 / (numPoints - 1); // Step size in normalized space
  const data = [];

  for (let i = 0; i < numPoints; i++) {
    // Calculate normalized time point
    const normalizedT = -1 + step * i;

    // Calculate S-curve value using logistic function
    const normalizedY = 1 / (1 + Math.exp(-steepness * (normalizedT - normalizedMidpoint)));

    // Scale Y value to the actual range
    const y = maxValue * normalizedY;
    const y0 = minValue !== undefined ? minValue * normalizedY : undefined;

    // Convert normalized time back to actual date
    const t = new Date(denormalizeTime(normalizedT));

    data.push({ x: t, y, y0 });
  }

  return data;
};

export const EvaChart: FC<EvaChartProps> = ({
  title,
  data,
  completionLogs,
  completionPercentage,
  plannedFee,
  startDate = null,
  deadline = null,
  currencySymbol,
}) => {
  console.log("deadline", deadline);
  if (data.length === 0) return null;
  const colourScheme = ["#1A1A1A", "#52C41A", "#2D2F95", "#FE0404"];
  const dashArray = ["10,5", "5,2"];

  const showCompletedWork = completionLogs.length > 0 || completionPercentage > 0;

  const plannedFeeAverage = isPriceRange(plannedFee)
    ? ((plannedFee.upper ?? 0) + (plannedFee.lower ?? 0)) / 2
    : plannedFee.amount;
  const costPerformanceIndex =
    (completionPercentage * plannedFeeAverage) / (data[data.length - 1].y * 100);
  const estimateAtCompletion =
    costPerformanceIndex === 0 ? undefined : plannedFeeAverage / costPerformanceIndex;

  // check if last date in completionLogs is today
  const lastLog =
    completionLogs.length > 0 ? new Date(completionLogs[completionLogs.length - 1].x) : null;
  const today = new Date();
  const isLastLogToday =
    lastLog?.getDate() === today.getDate() &&
    lastLog?.getMonth() === today.getMonth() &&
    lastLog?.getFullYear() === today.getFullYear();

  // if isLastLogToday is true, replace the last data point with completionPercentage, otherwise append it.
  let updatedCompletionLogs = isLastLogToday
    ? completionLogs.slice(0, -1).concat({ x: today, y: completionPercentage })
    : completionLogs.concat({ x: today, y: completionPercentage });

  const zeroPoint = {
    x: startDate ?? new Date(data[0].x),
    y: 0,
  };

  updatedCompletionLogs = [zeroPoint, ...updatedCompletionLogs];

  const maxFee = Math.max(
    ...data.map((d) => d.y),
    ...updatedCompletionLogs.map((d) => d.y),
    ...(deadline ? [estimateAtCompletion ?? 0] : [])
  );

  const maxTicks = 10;
  const range = Math.max(plannedFeeAverage, maxFee);
  const idealInterval = range / (maxTicks - 1);
  const roundedInterval =
    Math.ceil(idealInterval / (0.25 * plannedFeeAverage)) * (0.25 * plannedFeeAverage);

  const percentTickValues = Array.from(
    { length: Math.ceil(range / roundedInterval) },
    (_, i) => roundedInterval * (i + 1)
  );

  const scurveStartDate = new Date(data[0].x);
  const scurveEndDate = deadline ? new Date(deadline) : null;

  const legendData = [
    { name: "Accrued Fee", symbol: { fill: colourScheme[1], type: "square" as const } },
    ...(showCompletedWork
      ? [{ name: "Completed Work", symbol: { fill: colourScheme[2], type: "square" as const } }]
      : []),
    ...(scurveEndDate
      ? [{ name: "Planned Work", symbol: { fill: colourScheme[0], type: "square" as const } }]
      : []),
    ...(scurveEndDate && data.length > 0 && (completionLogs.length > 0 || completionPercentage > 0)
      ? [{ name: "Projection", symbol: { fill: colourScheme[3], type: "square" as const } }]
      : []),
  ];

  return (
    <>
      <div>
        <VictoryChart
          theme={VictoryTheme.grayscale}
          padding={{ top: 50, bottom: 150, left: 80, right: 50 }}
          width={500}
          height={400}
        >
          <VictoryLabel text={title} x={250} y={30} textAnchor="middle" />
          <VictoryAxis
            fixLabelOverlap
            tickFormat={(t: Date) => formatDate(new Date(t))}
            style={{
              ticks: { stroke: "grey", size: 5 },
              tickLabels: { angle: -45, textAnchor: "end" }, // Rotate labels
            }}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={(t) => formatCurrency(t, currencySymbol)}
            style={{ ticks: { stroke: "grey", size: 5 } }}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={(t) => `${Math.round((100 * t) / plannedFeeAverage)}%`}
            tickValues={percentTickValues}
            style={{ ticks: { stroke: "grey", size: 5 } }}
            orientation="right"
          />
          <VictoryGroup>
            {scurveEndDate && plannedFee && isPriceRange(plannedFee) && (
              <VictoryArea
                key={0}
                data={generateScurveData(scurveStartDate, scurveEndDate, plannedFee)}
                style={{ data: { fill: colourScheme[0], fillOpacity: 0.15 } }}
              />
            )}
            {scurveEndDate && plannedFee && !isPriceRange(plannedFee) && (
              <VictoryLine
                key={1}
                data={generateScurveData(scurveStartDate, scurveEndDate, plannedFee)}
                style={{ data: { stroke: colourScheme[0] } }}
              />
            )}
            <VictoryLine
              key={2}
              data={data.map((d) => ({ x: new Date(d.x), y: d.y }))}
              style={{
                data: {
                  stroke: colourScheme[1],
                  strokeDasharray: dashArray[0],
                },
              }}
            />
            {data[0] && showCompletedWork && (
              <VictoryLine
                key={3}
                data={updatedCompletionLogs.map((d) => ({
                  x: new Date(d.x),
                  y: (d.y * plannedFeeAverage) / 100,
                }))}
                style={{
                  data: {
                    stroke: colourScheme[2],
                    strokeDasharray: dashArray[1],
                  },
                }}
              />
            )}
            {data[0] && scurveEndDate && completionPercentage > 0 && (
              <VictoryLine
                key={4}
                data={[
                  { x: new Date(data[data.length - 1].x), y: data[data.length - 1].y },
                  { x: scurveEndDate, y: plannedFeeAverage / costPerformanceIndex },
                ]}
                style={{
                  data: {
                    stroke: colourScheme[3],
                    strokeDasharray: dashArray[1],
                  },
                }}
              />
            )}
          </VictoryGroup>
          <VictoryLegend
            x={legendData.length > 1 ? 130 : 200}
            y={330}
            orientation="horizontal"
            gutter={20}
            rowGutter={-5}
            itemsPerRow={2}
            style={{ border: { stroke: "black" } }}
            data={legendData}
            titleComponent={<></>}
          />
        </VictoryChart>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <p>
          Planned Value:{" "}
          {isPriceRange(plannedFee)
            ? formatCurrency(plannedFee.lower ?? 0, currencySymbol) +
              " - " +
              formatCurrency(plannedFee.upper ?? 0, currencySymbol)
            : formatCurrency(plannedFee.amount, currencySymbol)}
        </p>
        <p>CPI: {costPerformanceIndex.toFixed(2)}</p>
        {estimateAtCompletion && (
          <p>Estimate At Completion: {formatCurrency(estimateAtCompletion, currencySymbol)}</p>
        )}
      </div>
    </>
  );
};
